import { useElements, useStripe } from '@stripe/react-stripe-js'
import {
  PaymentIntent,
  StripeError,
} from '@stripe/stripe-js'
import { DonationRecurrenceInterval, PayPledgeInput, StripePaymentMethodType } from '~/types/graphql'

import { usePayPledgeMutation } from '../graphql/pay-pledge'
import { PaymentMethod } from '~/components/payment-form/types'
import type { Values } from '~/components/payment-form/hooks/use-form'

type Options = {
  pledgeWebId: string
  amountOverride?: number
  recurrenceOverride?: DonationRecurrenceInterval
  onSuccess: (amount: number, orderId: string) => void
}

enum Status {
  Succeeded = 'succeeded',
  Processing = 'processing',
}

export const usePayPledge = ({
  pledgeWebId,
  amountOverride,
  recurrenceOverride,
  onSuccess,
}: Options) => {
  const stripe = useStripe()
  const elements = useElements()
  const [payPledgeMutation] = usePayPledgeMutation()

  return async (recaptchaResponse: string, values: Values) => {
    if (!stripe || !elements) {
      return
    }

    const { error: submitError } = await elements.submit()

    if (submitError) {
      return
    }

    const paymentMethod =
      values.paymentMethod == PaymentMethod.Ach
        ? StripePaymentMethodType.BankAccount
        : StripePaymentMethodType.Card

    const orderAmount = values.coverProcessingFees
      ? values.orderCalculation.withCoveringFees
      : values.orderCalculation.withoutCoveringFees

    let clientSecret: string
    let orderId: string
    try {
      const input: PayPledgeInput = {
        pledgeWebId,
        recaptchaToken: recaptchaResponse,
        email: values.email.trim(),
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone || null,
        donorCoversProcessingFees: values.coverProcessingFees,
        allocationId: values.allocationId || null,
        stripePaymentMethod: paymentMethod,
        amount: amountOverride ?? null,
        recurrenceInterval: recurrenceOverride ?? null,
        address: values.address
          ? {
              firstName: values.firstName,
              lastName: values.lastName,
              phone: values.phone,
              ...values.address,
            }
          : null,
      }

      const { data } = await payPledgeMutation({ variables: { input } })
      clientSecret = data!.payPledge.clientSecret
      orderId = data!.payPledge.id
    } catch (error: any) {
      throw new Error(
        (error as any)?.graphQLErrors?.[0]?.message ?? 'Something Went Wrong',
      )
    }

    const result: { paymentIntent?: PaymentIntent; error?: StripeError } =
      await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: 'https://kindest.com',
        },
      })

    if (result.error) {
      throw new Error(result.error.message)
    }

    if (
      result.paymentIntent?.status == Status.Succeeded ||
      result.paymentIntent?.status == Status.Processing
    ) {
      onSuccess(orderAmount.net, orderId)
    }
  }
}
